import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { RebarAuthService } from './core/rebarauth/rebar.auth.service';
import { IdleTimeoutService } from './core/services/idle-timeout.service';
import { MasterService } from './core/services/master.service';
import { UserRoleService } from './core/services/user-role.service';
import { UserAccessData } from './core/models/user-access.model';
import { UserProfileComponent } from './user-profile/user-profile.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, UserProfileComponent],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('testAST_v20');
  currentYear: number = new Date().getFullYear();
  avatarError = false;
  userName = '';
  userPhoto: string | null = null;
  hasAccess = false;
  isCheckingAccess = true;
  isProfileReady = false;
  rollName = '';
  teamDescription = '';
  isHelpMenuOpen = false;
  showUserProfile = false;
  profileData: UserAccessData | null = null;
  isProfileLoading = false;

  private readonly destroying$ = new Subject<void>();
  private isFreshLogin = false;
  private deepLinkRestored = false;

  navItems: { name: string; route: string[] }[] = [];

  toggleHelpMenu(): void {
    this.isHelpMenuOpen = !this.isHelpMenuOpen;
  }

  closeHelpMenu(): void {
    this.isHelpMenuOpen = false;
  }

  openUserProfile(): void {
    this.isHelpMenuOpen = false;
    this.showUserProfile = true;
    const enterpriseId = this.rebarAuth.getUserName();
    if (!enterpriseId) return;
    this.isProfileLoading = true;
    this.masterService.getUserProfile(enterpriseId)
      .pipe(takeUntil(this.destroying$))
      .subscribe({
        next: (response: any) => {
          const data = Array.isArray(response) ? response[0] : (response?.recordset?.[0] ?? response);
          if (data) {
            this.profileData = {
              ...this.profileData,
              Email:        data.Email        ?? this.profileData?.Email,
              FirstName:    data.FirstName    ?? this.profileData?.FirstName,
              LastName:     data.LastName     ?? this.profileData?.LastName,
              EnterpriseID: data.EnterpriseID ?? this.profileData?.EnterpriseID,
              RoleName:     data.Role_Name    ?? this.profileData?.RoleName,
              IsActive:     data.IsActive     ?? this.profileData?.IsActive,
              Country:      data.Country      ?? this.profileData?.Country,
              WorkLocation: data.WorkLocation ?? this.profileData?.WorkLocation,
              ExpirationOn: data.ExpirationOn ?? this.profileData?.ExpirationOn,
            } as any;
          }
          this.isProfileLoading = false;
        },
        error: () => { this.isProfileLoading = false; }
      });
  }

  closeUserProfile(): void {
    this.showUserProfile = false;
  }

  logout(): void {
    this.rebarAuth.logout();
  }

  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (target instanceof Element && target.closest('.help-menu-item')) return;
    this.closeHelpMenu();
  }

  get isHelpSectionActive(): boolean {
    return this.router.url === '/help'
      || this.router.url.startsWith('/help/frequently-asked-questions')
      || this.router.url.startsWith('/help/macronize-timeline-calculator');
  }


  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private rebarAuth: RebarAuthService,
    private idleTimeout: IdleTimeoutService,
    private masterService: MasterService,
    private userRoleService: UserRoleService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.msalService.handleRedirectObservable().subscribe({
      next: (result) => { if (result) this.isFreshLogin = true; },
      error: (err) => console.error('MSAL handleRedirect error', err),
    });
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$)
      )
      .subscribe(() => this.loadUser());
  }

  get userInitial(): string {
    return (this.userName?.trim()?.charAt(0) || 'R').toUpperCase();
  }

  ngOnDestroy(): void {
    if (this.userPhoto) URL.revokeObjectURL(this.userPhoto);
    this.idleTimeout.stop();
    this.destroying$.next();
    this.destroying$.complete();
  }

  private async loadUser(): Promise<void> {
    const name = this.rebarAuth.getDisplayName();
    if (name) this.userName = name;

    if (this.rebarAuth.IsUserAuthenticated()) {
      this.idleTimeout.start();
      this.restorePreLoginUrl();
      this.checkApplicationAccess();
    }
    // If not authenticated: MSAL will trigger a login redirect — keep isCheckingAccess = true

    if (!this.userPhoto) {
      const photo = await this.rebarAuth.getUserPhotoUrl();
      if (photo) {
        this.userPhoto = photo;
        this.avatarError = false;
      }
    }
  }

  private restorePreLoginUrl(): void {
    const stored = sessionStorage.getItem(RebarAuthService.PRE_LOGIN_URL_KEY);
    if (!stored) return;
    sessionStorage.removeItem(RebarAuthService.PRE_LOGIN_URL_KEY);
    try {
      const target = new URL(stored);
      if (target.origin !== window.location.origin) return;
      const targetPath = target.pathname + target.search + target.hash;
      const currentPath = this.router.url;
      if (currentPath !== targetPath) {
        this.router.navigateByUrl(targetPath, { replaceUrl: true });
        this.deepLinkRestored = true;
      }
    } catch {
      // ignore invalid stored URL
    }
  }

  private checkApplicationAccess(): void {
    this.isCheckingAccess = true;
    const enterpriseId = this.rebarAuth.getUserName();
    if (!enterpriseId) {
      this.hasAccess = false;
      this.isCheckingAccess = false;
      this.isProfileReady = true;
      return;
    }
    this.masterService.getUserTabAccess(enterpriseId)
      .pipe(takeUntil(this.destroying$))
      .subscribe({
        next: (response: UserAccessData) => {
          this.profileData = response;
          this.hasAccess = !!response?.UserID;
          this.rollName = response?.RoleName ?? '';
          this.userRoleService.rollName = this.rollName;
          this.userRoleService.email = response?.Email ?? '';
          this.userRoleService.enterpriseId = response?.EnterpriseID ?? '';
          this.userRoleService.teams = response?.Teams ?? [];
          this.userRoleService.tabs = response?.Tabs ?? [];
          this.userRoleService.reviewTypes = response?.ReviewTypes ?? [];
          this.teamDescription = response?.Teams?.[0]?.TeamDescription ?? 'Audit Support Tool';
          this.navItems = (response?.Tabs ?? [])
            .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
            .filter(tab => !!tab.RouteUrl)
            .map(tab => ({ name: tab.TabName, route: [tab.RouteUrl] }));
          if (this.rollName === 'Administrator' && !this.navItems.some(item => item.route[0] === '/admin')) {
            const adminTab = { name: 'Admin', route: ['/admin'] };
            const reportIdx = this.navItems.findIndex(item => item.route[0] === '/report');
            const helpIdx = this.navItems.findIndex(item => item.route[0] === '/help');
            if (reportIdx !== -1) {
              this.navItems.splice(reportIdx + 1, 0, adminTab);
            } else if (helpIdx !== -1) {
              this.navItems.splice(helpIdx, 0, adminTab);
            } else {
              this.navItems.push(adminTab);
            }
          }
          this.isCheckingAccess = false;
          this.isProfileReady = true;
          if (this.isFreshLogin && this.hasAccess) {
            this.isFreshLogin = false;
            if (!this.deepLinkRestored) {
              this.router.navigate([this.userRoleService.isAdmin ? '/admin' : '/dashboard'], { replaceUrl: true });
            }
          }
        },
        error: () => {
          this.hasAccess = false;
          this.isCheckingAccess = false;
          this.isProfileReady = true;
        }
      });
  }
}
